// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import { ObservableOutput, SharedOrgOutput, ObservableTypeOutput, ActionOutput, PermissionsEnum } from 'thehive-sdk';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import { EntitySharingList } from '@Organisms/Shares/EntitySharingList/EntitySharingList';
import AnalyzerTable from '@Organisms/AnalyzerTable/AnalyzerTable';
import { EntitySharingCreate } from '@Organisms/Shares/EntitySharingCreate/EntitySharingCreate';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { ObservableReportTag } from './ObservableReportTag/ObservableReportTag';
import { formatTags } from '@Helpers/tags';
import { ObservableReportLevelColors } from '@Constants/colors';
import { SecurityTagList } from '@Molecules/SecurityTagList/SecurityTagList';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { normalizeDate } from '@Helpers/date';
import ResponderReports from '@Organisms/Report/ResponderReports/ResponderReports';
import ObservableLinkList from './ObservableLinkList/ObservableLinkList';
import { onCaseImageUpload, onOrganisationImageUpload } from '@Helpers/download';
import { Space } from 'antd';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { PermissionExtract } from '@Hooks/useSplitPermissions';

import './extractData.scss';

interface ExtractDataProps {
    data: ObservableOutput;
    eventList: ObservableEventList;
    locale: (...args: any[]) => any;
    hasPermissions: Record<
        PermissionExtract<
            [
                PermissionsEnum.MANAGE_OBSERVABLE,
                PermissionsEnum.MANAGE_SHARE,
                PermissionsEnum.MANAGE_ANALYSE,
                PermissionsEnum.MANAGE_ALERT_UPDATE,
            ]
        >,
        boolean
    >;
    organisation: string;
    shareList: SharedOrgOutput[];
    sharedOrgList: SharedOrgOutput[];
    caseId?: string;
    formEditMode?: boolean;
    visibleReportId?: string;
    isAlert?: boolean;
}

export const extractData = ({
    data,
    eventList,
    locale,
    hasPermissions,
    organisation,
    shareList,
    sharedOrgList = [],
    caseId,
    formEditMode,
    visibleReportId,
    isAlert = false,
}: ExtractDataProps): Record<string, ExtractProps> => {
    const { _id: observableId } = data;
    const isDisabled = isAlert ? !hasPermissions.observable || !hasPermissions.updateAlert : !hasPermissions.observable;
    return {
        '#computeSecurity': {
            isForce: true,
            className: 'observable-details__quarter',
            render: () => (
                <SecurityTagList
                    hasPermissions={
                        isAlert ? hasPermissions.observable && hasPermissions.updateAlert : hasPermissions.observable
                    }
                    hidden={['severity']}
                />
            ),
        },
        ioc: {
            className: 'observable-details__quarter',
            render: () => (
                <THForm.Item valuePropName="checked" name="ioc" label={locale('common.ioc')}>
                    <THSwitch disabled={isDisabled} />
                </THForm.Item>
            ),
        },
        sighted: {
            className: 'observable-details__quarter',
            render: () => (
                <THForm.Item valuePropName="checked" name="sighted" label={locale('common.sighted')}>
                    <THSwitch disabled={isDisabled} />
                </THForm.Item>
            ),
        },
        ignoreSimilarity: {
            className: 'observable-details__quarter',
            isForce: true,
            render: () => (
                <THForm.Item valuePropName="checked" name="ignoreSimilarity" label={locale('common.ignore-similarity')}>
                    <THSwitch disabled={isDisabled} />
                </THForm.Item>
            ),
        },
        '#computeTitle': {
            className: 'observable-details__section',
            render: (title: string) => (
                <THForm.Item label={locale('common.data')}>
                    <THTextArea
                        className="observable-details__textarea"
                        borderless
                        disabled
                        value={title}
                        rows={title.length > 128 ? 4 : 1}
                    />
                </THForm.Item>
            ),
        },
        '#computeDataType': {
            className: 'observable-details__section',
            render: ({
                observableTypeList,
                refetchObservableTypeList,
            }: {
                observableTypeList: ObservableTypeOutput[];
                refetchObservableTypeList: () => void;
            }) => (
                <THForm.Item label={locale('common.data-type')} name="dataType">
                    <THSelect
                        borderless
                        disabled={isDisabled}
                        options={observableTypeList.map(({ name }) => ({ label: name, value: name }))}
                        onDropdownVisibleChange={(open) => open && refetchObservableTypeList()}
                    />
                </THForm.Item>
            ),
        },
        'attachment.hashes': {
            isForce: false,
            className: 'observable-details__section',
            name: 'Hashes',
            render: (hashes: string[]) => (
                <Space direction="vertical">
                    <Space>
                        <span className="observable-details__hashes__title">SHA256</span>
                        {hashes[0]}
                    </Space>
                    <Space>
                        <span className="observable-details__hashes__title">SHA1</span>
                        {hashes[1]}
                    </Space>
                    <Space>
                        <span className="observable-details__hashes__title">MD5</span>
                        {hashes[2]}
                    </Space>
                </Space>
            ),
        },
        tags: {
            className: 'observable-details__section',
            isForce: true,
            render: () => (
                <THForm.Item name="tags" label={locale('common.tags')}>
                    <THTagSelector.Autocomplete borderless disabled={isDisabled} placeholder={locale('common.tags')} />
                </THForm.Item>
            ),
        },
        reports: {
            name: locale('common.reports'),
            className: 'observable-details__section',
            render: (reports: ObservableOutput['reports'] = {}) =>
                Object.keys(reports).length
                    ? Object.entries(reports).map(([analyzerId, { taxonomies }]) =>
                          taxonomies.map(({ level, namespace, predicate, value }, index) => (
                              <ObservableReportTag
                                  hasPermissions={hasPermissions.analyse}
                                  observableId={observableId}
                                  onGetLastReport={eventList.onGetLastReport}
                                  analyzerId={analyzerId}
                                  observable={data}
                                  eventList={caseId ? eventList : undefined}
                                  importObservable
                                  value={formatTags({ namespace, predicate, value: value + '' })}
                                  color={ObservableReportLevelColors[level]}
                                  key={`report-${index}`}
                              />
                          )),
                      )
                    : locale('observable.list.table.value.no-reports'),
        },
        sightedAt: {
            isForce: true,
            className: 'observable-details__section',
            render: () => (
                <THForm.Item<ObservableOutput> noStyle shouldUpdate={(prev, next) => prev.sighted !== next.sighted}>
                    {({ getFieldValue }) => (
                        <THForm.Item
                            name="sightedAt"
                            label={locale('common.sighted-at')}
                            normalize={normalizeDate}
                            hidden={!getFieldValue('sighted')}
                        >
                            <THDatePicker borderless disabled={isDisabled} placeholder={locale('common.sighted-at')} />
                        </THForm.Item>
                    )}
                </THForm.Item>
            ),
        },
        message: {
            className: 'observable-details__section',
            isForce: true,
            render: () => (
                <THForm.Item name="message" label={locale('common.description')}>
                    <THMarkdown
                        borderless
                        disabled={isDisabled}
                        onImageUpload={caseId ? onCaseImageUpload(caseId) : onOrganisationImageUpload}
                        formEditMode={formEditMode}
                    />
                </THForm.Item>
            ),
        },
        '#computeAnalyzer': {
            name: <TitleSection>{locale('common.analyzers')}</TitleSection>,
            className: 'observable-details__analyzers',
            render: ({ analyzerData, analysisData, observable }: any): ReactElement => (
                <AnalyzerTable
                    analyzers={analyzerData}
                    hasPermissions={hasPermissions.analyse}
                    observable={observable}
                    observableId={observableId}
                    visibleReportId={visibleReportId}
                    analysis={analysisData}
                    loading={false}
                    importObservable
                    eventList={eventList}
                />
            ),
        },

        '#computeResponderReportList': {
            name: <TitleSection>{locale('case.description.sider.responder-reports.title')}</TitleSection>,
            isForce: true,
            className: 'observable-details__reports',
            render: (reports: ActionOutput[]): ReactElement =>
                !!reports.length ? (
                    <ResponderReports reports={reports} columnsRenderer={columnsRenderer} />
                ) : (
                    <DisplayEmptyData label={locale('case.description.sider.responder-reports.no-reports')} />
                ),
        },

        '#computeObservableLinkList': {
            name: <TitleSection>{locale('observable.links.title')}</TitleSection>,
            className: 'observable-details__link',
            render: (similarData: ObservableOutput[]): ReactElement => (
                <ObservableLinkListOrEmptyData similarData={similarData} caseId={caseId} locale={locale} />
            ),
        },
        '#computeShares': {
            name: ({ setVisible }): ReactElement => (
                <TitleSection>
                    <div>{locale('sharing.title')}</div>
                    {eventList.shares.onAdd && eventList.shares.onCaseShareList && hasPermissions.share && (
                        <EntitySharingCreate
                            entityId={observableId}
                            onAdd={eventList.shares.onAdd}
                            onCaseShareList={eventList.shares.onCaseShareList}
                            currentOrganisation={organisation}
                            alreadySharedList={shareList}
                            caseId={caseId || ''}
                            onVisibleChange={setVisible}
                        />
                    )}
                </TitleSection>
            ),
            className: 'observable-details__shares',
            isForce: !!caseId,
            hidden: !sharedOrgList?.length,
            render: ({
                shareList = [],
                setVisible,
            }: {
                shareList: SharedOrgOutput[];
                setVisible: (visible: boolean) => void;
            }): ReactElement =>
                !!shareList.length ? (
                    <EntitySharingList
                        shareList={shareList}
                        caseId={caseId || ''}
                        entityId={observableId}
                        eventList={eventList.shares}
                        hasPermissions={hasPermissions.share}
                    />
                ) : (
                    <DisplayEmptyData label={locale('sharing.no-shares')}>
                        {eventList.shares.onAdd && eventList.shares.onCaseShareList && hasPermissions.share && (
                            <EntitySharingCreate
                                entityId={observableId}
                                onAdd={eventList.shares.onAdd}
                                onCaseShareList={eventList.shares.onCaseShareList}
                                currentOrganisation={organisation}
                                alreadySharedList={shareList}
                                caseId={caseId || ''}
                                onVisibleChange={setVisible}
                                triggerElem={<THButton type="link">{locale('sharing.add')}</THButton>}
                            />
                        )}
                    </DisplayEmptyData>
                ),
        },
    };
};

const ObservableLinkListOrEmptyData = ({
    similarData,
    locale,
    caseId,
}: {
    similarData: ObservableOutput[];
    locale: (...args: any[]) => any;
    caseId?: string;
}): ReactElement => {
    const filteredData = useMemo(
        () =>
            similarData.filter((obs) => {
                if (caseId !== undefined) {
                    const linkData = obs.extraData?.links?.observable;
                    const isSameEntity = linkData?.case?._id == caseId || linkData?.alert?._id == caseId;
                    return !isSameEntity;
                }
                return true;
            }),
        [similarData, caseId],
    );
    return !!filteredData.length ? (
        <ObservableLinkList similarData={filteredData} />
    ) : (
        <DisplayEmptyData label={locale('observable.links.noLinks')} />
    );
};
